/**
 * Time display, implemented once. A second implementation of any of this in an
 * app is a review-blocking defect — the whole point is that a relative age, its
 * tooltip, a duration and a range can never disagree with each other.
 *
 * The rules, and where each lives:
 *  1. Relative under 24h ("4m ago") -> `relativeTime`.
 *  2. Absolute at or beyond 24h, ALWAYS with the zone abbreviation ->
 *     `absoluteTime`. A timestamp without a zone is not checkable.
 *  3. Nullish renders as an em dash, never "null", never blank, never 1970 ->
 *     `EM_DASH`, returned by every function on nullish input.
 *  4. Durations "847ms" / "3m12s" / "1h04m", never decimal minutes ->
 *     `duration`.
 *  5. Every relative time carries its absolute alongside it -> `timeCell`
 *     returns both, so a caller cannot render one without having the other.
 *  6. Ranges "09:04 → 09:41 UTC (37m)" -> `timeRange`.
 *  7. Relative ages recompute on a shared 30s interval, suspended while the tab
 *     is hidden -> `useNow` (the interval, not the formatting).
 *
 * Two conventions apply throughout. Formatting is Intl-only — no pattern
 * strings, no date library — and every Intl.DateTimeFormat is HOISTED into a
 * module-scope cache keyed by its options, because construction measures ~52x
 * the cost of reuse and a 500-row table would otherwise build 500 formatters.
 * Every function that renders a wall-clock time takes an explicit
 * `timeZone?: string`, so a caller can be timezone-explicit rather than
 * silently inheriting the host zone.
 *
 * Note the deliberate asymmetry with @ffn/components' date family: THIS
 * module is about instants (a timestamp has a zone), while Calendar/DatePicker
 * are about plain dates (which have none). Components never import this.
 */

import { useEffect, useState } from "react"

/** Rule 3: the one nullish rendering. Its colour is a caller concern. */
export const EM_DASH = "—"

/** Rule 7: relative ages recompute every 30s. */
export const TICK_MS = 30_000

const MINUTE = 60
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * Parse a timestamp to a Date, or null for anything unusable: nullish, empty,
 * or an unparseable string. Returning null rather than throwing is what lets
 * every public function honour rule 3 with a single early return.
 */
const parseInstant = (value: string | null | undefined): Date | null => {
  if (value === null || value === undefined || value === "") return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * The module-scope formatter cache. Intl.DateTimeFormat construction is the
 * expensive part (~82µs vs ~1.6µs to reuse one), and these formatters are pure
 * functions of their options, so one instance per distinct option set serves
 * the whole process. A useMemo would be per-component and defeat the point.
 */
const formatters = new Map<string, Intl.DateTimeFormat>()

const formatter = (locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const key = `${locale}|${JSON.stringify(options)}`
  const cached = formatters.get(key)
  if (cached !== undefined) return cached
  const made = new Intl.DateTimeFormat(locale, options)
  formatters.set(key, made)
  return made
}

/**
 * The zone abbreviation ("UTC", "CDT") for an instant. Rule 2 makes this
 * mandatory on every absolute time, so it is derived from the same formatter
 * machinery as the timestamp itself and can never disagree with it.
 *
 * `timeZoneName: "short"` alone would produce a time string we do not want, so
 * the value is pulled out of `formatToParts` rather than parsed back out of a
 * formatted string.
 */
const zoneAbbrev = (date: Date, timeZone?: string): string => {
  const parts = formatter("en-US", {
    timeZoneName: "short",
    ...(timeZone !== undefined ? { timeZone } : {})
  }).formatToParts(date)
  return parts.find((part) => part.type === "timeZoneName")?.value ?? ""
}

/**
 * Rule 1: "just now" under 10s, then "42s ago" / "4m ago" / "3h ago" /
 * "2d ago". A future timestamp is a real thing (a scheduled next run), so it
 * reads "in 5m" rather than as a negative age.
 *
 * `now` is a parameter, not a `Date.now()` call inside, so a caller can drive
 * every age on a page from one shared clock (`useNow`) and a test can pin it.
 */
export function relativeTime(value: string | null | undefined, now = Date.now()): string {
  const date = parseInstant(value)
  if (date === null) return EM_DASH
  const seconds = Math.round((now - date.getTime()) / 1000)
  if (seconds < 0) {
    const ahead = -seconds
    if (ahead < MINUTE) return `in ${ahead}s`
    if (ahead < HOUR) return `in ${Math.round(ahead / MINUTE)}m`
    if (ahead < DAY) return `in ${Math.round(ahead / HOUR)}h`
    return `in ${Math.round(ahead / DAY)}d`
  }
  if (seconds < 10) return "just now"
  if (seconds < MINUTE) return `${seconds}s ago`
  if (seconds < HOUR) return `${Math.floor(seconds / MINUTE)}m ago`
  if (seconds < DAY) return `${Math.floor(seconds / HOUR)}h ago`
  return `${Math.floor(seconds / DAY)}d ago`
}

/**
 * Rule 2: "14 Aug 2026 03:00 UTC" — day, short month, year, 24h clock, zone.
 * The zone abbreviation is not optional and not a tooltip: an absolute time a
 * reader cannot place in a zone is not a fact they can check.
 *
 * `hourCycle: "h23"` and NEVER `hour12` alongside it — when both are present
 * `hour12` silently wins and a midnight renders as "24:00".
 */
export function absoluteTime(value: string | null | undefined, timeZone?: string): string {
  const date = parseInstant(value)
  if (date === null) return EM_DASH
  const text = formatter("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    ...(timeZone !== undefined ? { timeZone } : {})
  }).format(date)
  return `${text.replace(",", "")} ${zoneAbbrev(date, timeZone)}`
}

/**
 * Clock only, no date: "09:04". Used inside a range whose date is stated once
 * by its caller — never as a standalone timestamp, which would violate rule 2.
 */
export function clockTime(value: string | null | undefined, timeZone?: string): string {
  const date = parseInstant(value)
  if (date === null) return EM_DASH
  return formatter("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    ...(timeZone !== undefined ? { timeZone } : {})
  }).format(date)
}

/**
 * Rule 4: "847ms" / "42s" / "3m12s" / "1h04m". Never decimal minutes — "1.7m"
 * is a number, not a duration a reader can hold.
 *
 * Nullish (not 0) is the unfinished case: a zero duration reads as instant
 * completion, which is a different fact from "still running".
 */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return EM_DASH
  if (seconds < 0) return EM_DASH
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`
  const total = Math.floor(seconds)
  if (total < MINUTE) return `${total}s`
  if (total < HOUR) {
    const minutes = Math.floor(total / MINUTE)
    return `${minutes}m${String(total % MINUTE).padStart(2, "0")}s`
  }
  const hours = Math.floor(total / HOUR)
  const minutes = Math.floor((total % HOUR) / MINUTE)
  return `${hours}h${String(minutes).padStart(2, "0")}m`
}

/** The gap between two timestamps as a duration. Nullish-safe on either side. */
export function gap(from: string | null | undefined, to: string | null | undefined): string {
  const start = parseInstant(from)
  const end = parseInstant(to)
  if (start === null || end === null) return EM_DASH
  return duration((end.getTime() - start.getTime()) / 1000)
}

/**
 * Rule 6: "09:04 → 09:41 UTC (37m)". One zone label for the pair, because both
 * ends are rendered in the same zone and repeating it would imply otherwise.
 * An open range (no end yet) keeps the zone and em-dashes the end.
 */
export function timeRange(
  from: string | null | undefined,
  to: string | null | undefined,
  timeZone?: string
): string {
  const start = parseInstant(from)
  if (start === null) return EM_DASH
  const zone = zoneAbbrev(start, timeZone)
  const end = parseInstant(to)
  if (end === null) return `${clockTime(from, timeZone)} → ${EM_DASH} ${zone}`
  const span = duration((end.getTime() - start.getTime()) / 1000)
  return `${clockTime(from, timeZone)} → ${clockTime(to, timeZone)} ${zone} (${span})`
}

/** What a cell renders: the visible text plus its mandatory absolute tooltip. */
export interface TimeCell {
  /** The visible text. */
  readonly text: string
  /** The tooltip content. Rule 5 — no exceptions. */
  readonly title: string
  /** True when `text` is relative, so a caller can pick the right face. */
  readonly isRelative: boolean
}

/**
 * Rules 1 + 2 + 5 together: relative under 24h, absolute at or beyond it, and
 * ALWAYS the exact instant in `title`. A relative age with no route to the
 * exact instant is a forensics dead end, which is why this returns both — a
 * caller physically cannot render the text without having been handed the
 * tooltip.
 */
export function timeCell(
  value: string | null | undefined,
  now = Date.now(),
  timeZone?: string
): TimeCell {
  const date = parseInstant(value)
  if (date === null) return { text: EM_DASH, title: "not set", isRelative: false }
  const absolute = absoluteTime(value, timeZone)
  const ageSeconds = (now - date.getTime()) / 1000
  if (ageSeconds >= 0 && ageSeconds < DAY) {
    return { text: relativeTime(value, now), title: absolute, isRelative: true }
  }
  return { text: absolute, title: absolute, isRelative: false }
}

/**
 * Start a shared clock: call `onTick` every `intervalMs` while the tab is
 * visible, and return the teardown. Suspended on `document.hidden` (a
 * background tab recomputing ages is pure waste) and re-synced immediately on
 * `visibilitychange`, so nothing is stale at the moment it becomes visible.
 *
 * This is the imperative core `useNow` wraps. It exists separately because
 * app-kit depends on `react` but not `react-dom` and so has no renderer: the
 * listener lifecycle and the hidden-tab suspension are provable here without a
 * `renderHook`. It is NOT part of the package barrel — the hook is the public
 * surface, this is the tested one.
 */
export const startClock = (intervalMs: number, onTick: () => void): (() => void) => {
  if (typeof document === "undefined") return () => {}
  let timer: ReturnType<typeof setInterval> | null = null
  const start = (): void => {
    if (timer !== null) return
    timer = setInterval(onTick, intervalMs)
  }
  const stop = (): void => {
    if (timer !== null) clearInterval(timer)
    timer = null
  }
  const onVisibility = (): void => {
    if (document.hidden) stop()
    else {
      onTick()
      start()
    }
  }
  if (!document.hidden) start()
  document.addEventListener("visibilitychange", onVisibility)
  return () => {
    stop()
    document.removeEventListener("visibilitychange", onVisibility)
  }
}

/**
 * Rule 7: one clock for every relative age on screen.
 *
 * Pass the result into `relativeTime`/`timeCell` as their `now`, so a page's
 * ages all step together instead of each row owning a timer. Text swap only,
 * never an animation.
 */
export function useNow(intervalMs = TICK_MS): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => startClock(intervalMs, () => setNow(Date.now())), [intervalMs])
  return now
}
